import {
  Alert,
  AlertIcon,
  AlertProps,
  AlertTitle,
  Button,
  ButtonProps,
  HStack,
  SpinnerProps,
} from "@chakra-ui/react";
import { UseQueryResult } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useMemo } from "react";

import { translateError } from "@/common/utils/errorHandling";
import { ErrorResponse } from "@reflag/shared/api";

import { DelayedSpinner } from "./DelayedSpinner";

interface QueryDataGuardProps<TData> {
  query: UseQueryResult<TData, AxiosError<ErrorResponse>>;
  allowRetry?: boolean;
  errorTitle?: string;
  _spinner?: SpinnerProps;
  _error?: AlertProps;
  _button?: Omit<ButtonProps, "isLoading" | "onClick" | "isDisabled">;
  render: (data: TData) => React.ReactNode;
}

export default function QueryDataGuard<TData>({
  render,
  query,
  errorTitle = "Failed to load data",
  allowRetry = true,
  _spinner,
  _error,
  _button,
}: QueryDataGuardProps<TData>) {
  const errorMessage = useMemo(() => {
    if (query.error) {
      const { title } = translateError(query.error, errorTitle);
      return title;
    }
  }, [query.error, errorTitle]);

  if (query.isLoading && !query.isRefetching) {
    return <DelayedSpinner {..._spinner} />;
  }

  if (!query.isSuccess || !query.data) {
    return (
      <Alert status="error" {..._error}>
        <HStack>
          <AlertIcon />
          <AlertTitle>
            {errorMessage ?? "Failed to load data. Please try again."}
          </AlertTitle>
        </HStack>
        {allowRetry && (
          <Button
            isLoading={query.isRefetching}
            variant="outline"
            {..._button}
            onClick={() => query.refetch()}
          >
            Retry
          </Button>
        )}
      </Alert>
    );
  }

  return render(query.data);
}
